import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Settings, X } from 'lucide-react'
import { useCookieConsent, type CookiePreferences } from '@/contexts/CookieConsentContext'
import { LocalizedLink } from './LocalizedLink'

export function CookieConsent() {
  const { t } = useTranslation()
  const { showBanner, acceptAll, rejectAll, savePreferences } = useCookieConsent()
  const [showPreferences, setShowPreferences] = useState(false)
  const [tempPrefs, setTempPrefs] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  if (!showBanner) return null

  return (
    <>
      {/* Banner */}
      {!showPreferences && (
        <div
          className="fixed bottom-4 left-4 right-4 md:right-auto md:max-w-sm bg-[#F5EDE8] shadow-2xl shadow-black/10 border border-[#4E2118]/8 overflow-hidden animate-in slide-in-from-bottom-5 fade-in duration-300"
          style={{ zIndex: 9999 }}
          role="dialog"
          aria-label={t('cookie.title', 'Cookie consent')}
        >
          <div className="h-px bg-[#DF631E]" />
          <div className="p-5">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="h-px w-6 bg-[#DF631E]" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#DF631E]">Cookie</span>
            </div>
            <p className="text-sm text-[#4E2118]/80 mb-4 leading-relaxed">
              {t('cookie.message')}{' '}
              <LocalizedLink
                to="privacy"
                className="font-semibold underline underline-offset-2 hover:opacity-80 transition-opacity text-[#4E2118]"
              >
                {t('cookie.learnMore')}
              </LocalizedLink>
            </p>
            <div className="flex gap-2">
              <button
                onClick={acceptAll}
                className="flex-1 py-2 px-3 text-sm font-semibold border-none cursor-pointer transition-all bg-[#DF631E] text-white hover:bg-[#B85018]"
              >
                {t('cookie.acceptAll', 'Accetta tutti')}
              </button>
              <button
                onClick={rejectAll}
                className="flex-1 py-2 px-3 text-sm font-semibold border cursor-pointer transition-all bg-transparent border-[#4E2118]/20 text-[#4E2118]/70 hover:bg-[#4E2118]/5 hover:border-[#4E2118]/30"
              >
                {t('cookie.rejectAll', 'Rifiuta')}
              </button>
            </div>
            <button
              onClick={() => setShowPreferences(true)}
              className="mt-3 flex items-center justify-center gap-1.5 w-full text-xs text-[#4E2118]/40 hover:text-[#4E2118]/70 transition-colors"
            >
              <Settings className="h-3.5 w-3.5" />
              {t('cookie.customize', 'Personalizza')}
            </button>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showPreferences && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black/50 p-4 animate-in fade-in duration-200"
          style={{ zIndex: 10000 }}
          onClick={() => setShowPreferences(false)}
        >
          <div
            className="bg-[#F5EDE8] shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200 border border-[#4E2118]/8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-px bg-[#DF631E]" />
            <div className="p-6">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2.5">
                  <span className="h-px w-6 bg-[#DF631E]" />
                  <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#DF631E]">
                    {t('cookie.customize', 'Personalizza')}
                  </span>
                </div>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="p-1 text-[#4E2118]/40 hover:text-[#4E2118] transition-colors"
                  aria-label={t('aria.close', 'Chiudi')}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <h2 className="text-lg font-semibold text-[#4E2118] mb-1 mt-3">
                {t('cookie.preferencesTitle', 'Preferenze Cookie')}
              </h2>
              <p className="text-sm text-[#4E2118]/60 mb-5">
                {t('cookie.preferencesDescription', 'Gestisci le tue preferenze sui cookie. I cookie necessari sono sempre attivi.')}
              </p>

              <div className="space-y-px">
                {/* Necessary */}
                <div className="flex items-center justify-between p-3 bg-white/50 border border-[#4E2118]/6">
                  <div>
                    <p className="font-medium text-sm text-[#4E2118]">{t('cookie.necessary', 'Necessari')}</p>
                    <p className="text-xs text-[#4E2118]/50">{t('cookie.necessaryDesc', 'Essenziali per il funzionamento del sito.')}</p>
                  </div>
                  <span className="text-xs text-[#DF631E] font-medium">{t('cookie.alwaysOn', 'Sempre attivi')}</span>
                </div>

                {/* Analytics */}
                <div className="flex items-center justify-between p-3 bg-white/50 border border-[#4E2118]/6">
                  <div className="pr-3">
                    <p className="font-medium text-sm text-[#4E2118]">{t('cookie.analytics', 'Analitici')}</p>
                    <p className="text-xs text-[#4E2118]/50">{t('cookie.analyticsDesc', 'Ci aiutano a capire come usi il sito.')}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={tempPrefs.analytics}
                    aria-label={t('cookie.analytics', 'Analitici')}
                    onClick={() => setTempPrefs((p) => ({ ...p, analytics: !p.analytics }))}
                    className={`relative shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      tempPrefs.analytics ? 'bg-[#DF631E]' : 'bg-[#4E2118]/15'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
                        tempPrefs.analytics ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>

                {/* Marketing */}
                <div className="flex items-center justify-between p-3 bg-white/50 border border-[#4E2118]/6">
                  <div className="pr-3">
                    <p className="font-medium text-sm text-[#4E2118]">{t('cookie.marketing', 'Marketing')}</p>
                    <p className="text-xs text-[#4E2118]/50">{t('cookie.marketingDesc', 'Per contenuti e pubblicità personalizzati.')}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={tempPrefs.marketing}
                    aria-label={t('cookie.marketing', 'Marketing')}
                    onClick={() => setTempPrefs((p) => ({ ...p, marketing: !p.marketing }))}
                    className={`relative shrink-0 inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      tempPrefs.marketing ? 'bg-[#DF631E]' : 'bg-[#4E2118]/15'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${
                        tempPrefs.marketing ? 'translate-x-5' : 'translate-x-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="mt-5 pt-5 border-t border-[#4E2118]/8 flex gap-2 justify-end">
                <button
                  onClick={() => setShowPreferences(false)}
                  className="py-2 px-4 text-sm font-medium text-[#4E2118]/50 hover:text-[#4E2118] hover:bg-[#4E2118]/5 transition-colors"
                >
                  {t('cookie.cancel', 'Annulla')}
                </button>
                <button
                  onClick={() => {
                    savePreferences(tempPrefs)
                    setShowPreferences(false)
                  }}
                  className="py-2 px-4 text-sm font-semibold cursor-pointer transition-all bg-[#DF631E] text-white hover:bg-[#B85018]"
                >
                  {t('cookie.savePreferences', 'Salva preferenze')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
